import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { analyzeIdea, precheckIdea } from "@/lib/api";

export function IdeaEntry({ compact = false, variant = "founder" }) {
  const nav = useNavigate();
  const business = variant === "business";
  const [idee, setIdee] = useState("");
  const [busy, setBusy] = useState(false);
  const [followup, setFollowup] = useState("");
  const [askedOnce, setAskedOnce] = useState(false);
  const status = business ? "laeuft" : "idee";

  const hints = business
    ? ["Welches Angebot hast du am Markt?", "Wer ist deine aktuelle Zielgruppe?", "Wo siehst du aktuell dein größtes Optimierungspotenzial oder deinen Engpass?"]
    : ["Was ist dein Produkt/Angebot?", "Wie hebst du dich ab?", "Löst du ein Problem, wenn ja welches?"];

  const start = async () => {
    const text = idee.trim();
    if (!text) {
      setFollowup("Damit dein Report konkret wird, beschreibe dein Vorhaben bitte noch etwas genauer. Was genau bietest du an, was ist dein Konzept und was macht dein Angebot besonders?");
      return;
    }
    setBusy(true);
    try {
      // Interne Qualitätsprüfung – aber nur EINMAL. Nach der ersten Rückfrage geht es
      // beim nächsten Klick in jedem Fall weiter. Fail-open bei Fehlern.
      if (!askedOnce) {
        let gate = { sufficient: true };
        try {
          gate = await precheckIdea(text, status, business ? "business" : "gruender");
        } catch {
          gate = { sufficient: true };
        }
        if (!gate.sufficient) {
          setFollowup(gate.message || "Damit dein Report konkret wird, beschreibe dein Vorhaben bitte noch etwas genauer.");
          setAskedOnce(true);
          setBusy(false);
          return;
        }
      }
      setFollowup("");
      const res = await analyzeIdea(text, status, business ? "business" : "gruender");
      nav(`/teaser/${res.report_id}`, {
        state: { intro: res.intro, signals: res.signals, blocked: res.blocked },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ersteinschätzung konnte nicht gestartet werden.");
      setBusy(false);
    }
  };

  return (
    <div data-testid="idea-entry" className={compact ? "" : "bg-white rounded-3xl border border-slate-200/70 shadow-xl p-6 sm:p-8"}>
      <div data-testid="idea-hints" className="mb-2.5 flex flex-wrap gap-2">
        {hints.map((h) => (
          <span
            key={h}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-[11px] font-medium text-slate-600"
          >
            <span className="h-1.5 w-1.5 rounded-full brand-gradient-bg shrink-0" />
            {h}
          </span>
        ))}
      </div>
      <Textarea
        data-testid="idea-input"
        value={idee}
        onChange={(e) => { setIdee(e.target.value); if (followup) setFollowup(""); }}
        placeholder="Je genauer du beschreibst, desto besser ist das Ergebnis."
        className="min-h-[150px] rounded-xl text-[15px] bg-white"
      />
      {followup && (
        <div
          data-testid="idea-followup"
          className="mt-3 rounded-xl px-4 py-3 text-sm leading-relaxed border flex items-start gap-2.5"
          style={{ background: "var(--brand-tint)", borderColor: "var(--brand-solid)", color: "#0f172a" }}
        >
          <Sparkles className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--brand-solid)" }} />
          <span>{followup}</span>
        </div>
      )}
      <Button
        data-testid="idea-start-btn"
        onClick={start}
        disabled={busy}
        className="pulse-cta mt-5 h-14 w-full rounded-xl font-semibold text-base brand-gradient-bg text-white hover:opacity-95 shadow-lg whitespace-normal leading-snug"
      >
        {busy ? (
          <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> KI prüft {business ? "dein Business" : "deine Idee"} …</>
        ) : (
          <>Kostenlose Ersteinschätzung starten <ArrowRight className="ml-2 h-5 w-5 shrink-0" /></>
        )}
      </Button>
    </div>
  );
}
